import React, {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  BaseReferenceDto,
  DocumentDto,
  SearchDocument as Document,
} from '@cfa/pathway-fe-shared/xp';
import { Plan } from '@cfa/pathway-fe-shared/xp';
import { IconButton } from '@cfa/react-components';

import useGetStructuredBrowseData, {
  UnkeyedCategory,
  UnkeyedDocument,
  UnkeyedSubcategory,
} from '@/hooks/useGetStructuredBrowseData';
import { useScrollMemory } from '@/hooks/useScrollMemory';

import { isApiError } from '@/utils/request';
import { cn } from '@/utils/tailwind';

import DocumentPreview from '@/components/DocumentPreview/DocumentPreview';
import GenericError from '@/components/Error/GenericError';
import PathwayModal from '@/components/popups/PathwayModal';
import Searchbar from '@/components/Searchbar/Searchbar';

import { AddProcedureFooter } from './components/AddProcedureFooter';
import StepOneCategories from './components/StepOneCategories';
import StepThreeSearchResults from './components/StepThreeSearchResults';
import StepTwoSubcategories from './components/StepTwoSubcategories';
import { PROCEDURE_STEPS } from './constants';
import { PopupDocument } from './types';

import { IntlSubcategoryDto } from '@/_generated/api-clients/content';
import { store } from '@/atoms';
import { useSearchDispatch } from '@/atoms/search';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { useProcedureState } from '@/routes/TrainingPlans/BuildPlans/hooks/useProcedureState';

export type ProcedureStep =
  (typeof PROCEDURE_STEPS)[keyof typeof PROCEDURE_STEPS];

interface AddProcedurePopupProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Plan;
  isInSection?: boolean;
  sectionId?: string;
  onCustomAddProcedure?: (selectedDocuments: PopupDocument[]) => void;
  adminAddingProcedure?: boolean;
  selectedCountryCode?: string; // country code value will only exist if imported from Admin component and is Admin user
  onlyAllowOneDoc?: boolean;
  textVariant?: 'update' | 'default';
}

const createStepKey = (
  stepId: number,
  selections: Record<string, unknown>,
): string => {
  const keys = Object.keys(selections)
    .filter(k => selections[k] != null)
    .sort();

  const parts = [`step:${stepId}`];
  for (const k of keys) {
    let v = selections[k];
    if (typeof v === 'boolean') v = v ? 1 : 0;
    parts.push(`${k}:${String(v)}`);
  }
  return parts.join('|');
};

const AddProcedurePopup: React.FC<AddProcedurePopupProps> = ({
  isOpen,
  onClose,
  plan,
  isInSection = false,
  sectionId = '',
  onCustomAddProcedure,
  adminAddingProcedure = false,
  selectedCountryCode,
  onlyAllowOneDoc = false,
  textVariant = 'default',
}) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const searchDispatch = useSearchDispatch();

  const planId = plan?.id;

  // Draft persistence - all persisted state managed by hook
  const {
    activeStep,
    setActiveStep,
    search,
    setSearch,
    selectedDocuments,
    setSelectedDocuments,
    categorySelected,
    setCategorySelected,
    categoryName,
    setCategoryName,
    preservedSubcategoryIds,
    setPreservedSubcategoryIds,
    previewDocument,
    setPreviewDocument,
    resetDraft,
  } = useProcedureState({
    isOpen,
    planId,
    sectionId,
    isInSection,
  });

  // Local UI state (not persisted)
  const [isTridionPreview, setIsTridionPreview] = useState(false);
  const [subcategories, setSubcategories] = useState<
    UnkeyedSubcategory[] | IntlSubcategoryDto[] | null
  >(null);
  const [browseCategories, setBrowseCategories] = useState<
    UnkeyedCategory[] | null
  >(null);

  // Derived values
  const isPreviewResource = activeStep === PROCEDURE_STEPS.PREVIEW;

  const stepData = (() => {
    const selections: Record<string, string | undefined> = {};
    const category = categorySelected as UnkeyedCategory | null;

    if (activeStep === PROCEDURE_STEPS.SUBCATEGORIES && category?.id) {
      selections.categoryId = category.id;
    }
    if (activeStep === PROCEDURE_STEPS.SEARCH && search.query) {
      selections.query = search.query.trim().toLowerCase();
    }
    if (
      activeStep === PROCEDURE_STEPS.PREVIEW &&
      'id' in previewDocument &&
      typeof previewDocument.id === 'string'
    ) {
      selections.documentId = previewDocument.id;
    }
    return selections;
  })();

  const categories = browseCategories
    ? browseCategories.filter(category => !category.featured)
    : [];

  const featured = browseCategories
    ? browseCategories.filter(category => category.featured)
    : [];

  // Action handlers
  const handleSearchTermSelect = (
    suggestion: string | null,
    spellingSuggestion?: string,
  ) => {
    const termToUse =
      spellingSuggestion ?? suggestion ?? search.displayed.trim();
    if (termToUse.length) {
      setSearch({
        ...search,
        displayed: termToUse,
        query: termToUse,
      });
      setActiveStep(PROCEDURE_STEPS.SEARCH);
    }
  };

  const handleBackButtonClick = (onCloseCallback?: () => void) => {
    if (search.query && activeStep === PROCEDURE_STEPS.SEARCH) {
      setSearch({ ...search, query: '' });
      setActiveStep(PROCEDURE_STEPS.CATEGORIES);
      return;
    }
    if (search.query && activeStep === PROCEDURE_STEPS.PREVIEW) {
      setActiveStep(PROCEDURE_STEPS.SEARCH);
      return;
    }
    if (!search.query && activeStep === PROCEDURE_STEPS.PREVIEW) {
      setActiveStep(PROCEDURE_STEPS.SUBCATEGORIES);
      return;
    }
    if (activeStep > PROCEDURE_STEPS.CATEGORIES) {
      setActiveStep((activeStep - 1) as ProcedureStep);
    } else {
      onCloseCallback?.();
    }
  };

  // Use useEffectEvent for effects that need latest values without re-runs
  const handleResetSearch = useEffectEvent(() => {
    searchDispatch.reset();
  });

  const handleSetBrowseCategories = useEffectEvent(
    (categories: UnkeyedCategory[] | null) => {
      setBrowseCategories(categories);
    },
  );

  useEffect(() => {
    handleResetSearch();
  }, []);

  const { data: browseCategoriesData, error: browseError } =
    useGetStructuredBrowseData({
      structure: 'unkeyed',
    });

  // Update browseCategories when data changes
  useEffect(() => {
    handleSetBrowseCategories(browseCategoriesData ?? null);
  }, [browseCategoriesData]);

  const handleOnClose = () => {
    onClose();
    searchDispatch.reset();
    setIsTridionPreview(false);
    setSubcategories(null);
    resetDraft();
  };

  const scrollMemoryKey = createStepKey(activeStep, stepData);
  useScrollMemory({ container: modalBodyRef, key: scrollMemoryKey });

  const { t } = useTranslation();
  const { language } = store.get(userPrefsAtom);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const previewDocumentName =
    typeof previewDocument.name === 'string'
      ? (previewDocument.name as React.ReactNode)
      : typeof previewDocument.name === 'object'
        ? (previewDocument as DocumentDto).name?.[language]
        : undefined;
  const onClearSearchClick = useCallback(() => {
    setSearch({ ...search, displayed: '' });
    searchInputRef.current?.focus();
  }, [search, setSearch]);

  if (isApiError(browseError)) {
    datadogRum.addError(browseError, { section: 'browseCategories' });
    return <GenericError />;
  }
  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={handleOnClose}
      showCloseButton
      stepKey={String(activeStep) + 'addresource'}
      className="lg:max-w-[840px]!"
      bodyRef={modalBodyRef}
      fullHeight
    >
      <PathwayModal.Header>
        <PathwayModal.Title>
          {isPreviewResource
            ? t('TrainingPlans.preview')
            : textVariant === 'update'
              ? t('Generic.updateResource')
              : t('Generic.chooseResources')}
        </PathwayModal.Title>
        <div className="pb-4">
          {activeStep > PROCEDURE_STEPS.CATEGORIES && (
            <IconButton
              data-testid="BackButton"
              onClick={() => handleBackButtonClick(handleOnClose)}
              className="absolute! top-1! left-3!"
            >
              <IconArrowLeft />
            </IconButton>
          )}
          {isPreviewResource ? (
            <p className="text-variant-h2 text-center">{previewDocumentName}</p>
          ) : (
            <Searchbar
              className="z-5 mt-6 mb-2"
              elevation={1}
              onChange={e =>
                setSearch({
                  ...search,
                  displayed: e.target.value.length ? e.target.value : '',
                })
              }
              onClear={onClearSearchClick}
              onSubmit={term => {
                handleSearchTermSelect(term);
              }}
              placeholder={t('TrainingPlans.addResource.placeholder')}
              ref={searchInputRef}
              searchValue={search.displayed}
              showRecentSearchDropdown
              data-testid={
                textVariant === 'update'
                  ? 'UpdateResourceSearchbar'
                  : 'AddResourceSearchbar'
              }
            />
          )}
        </div>
      </PathwayModal.Header>
      <PathwayModal.Body
        noMinHeight={activeStep === PROCEDURE_STEPS.SUBCATEGORIES}
        className={cn(
          activeStep === PROCEDURE_STEPS.PREVIEW && 'bg-(--documentBackground)',
        )}
      >
        <div
          data-testid={
            textVariant === 'update'
              ? 'UpdateResourceModal'
              : 'AddResourceModal'
          }
          style={{
            background: isPreviewResource
              ? 'var(--documentBackground)'
              : 'transparent',
          }}
        >
          <div
            className={cn(
              'text-left',
              activeStep === PROCEDURE_STEPS.PREVIEW && isTridionPreview
                ? 'my-0'
                : 'my-4',
            )}
          >
            {activeStep === PROCEDURE_STEPS.CATEGORIES && (
              <StepOneCategories
                categories={categories}
                featured={featured}
                setCategorySelected={
                  setCategorySelected as (
                    category: UnkeyedCategory | null,
                  ) => void
                }
                setSearchTermDisplayed={value =>
                  setSearch({ ...search, displayed: value })
                }
                setActiveStep={setActiveStep}
              />
            )}
            {activeStep === PROCEDURE_STEPS.SUBCATEGORIES && (
              <StepTwoSubcategories
                categorySelected={categorySelected as UnkeyedCategory | null}
                preservedSubcategoryIds={preservedSubcategoryIds}
                setPreservedSubcategoryIds={setPreservedSubcategoryIds}
                subcategories={subcategories}
                categoryName={categoryName}
                setSubcategories={setSubcategories}
                setCategoryName={setCategoryName}
                setPreviewDocument={
                  setPreviewDocument as (
                    doc: Document | BaseReferenceDto | UnkeyedDocument,
                  ) => void
                }
                setActiveStep={setActiveStep}
                selectedCountryCode={selectedCountryCode}
                adminAddingProcedure={adminAddingProcedure}
                selectedDocuments={selectedDocuments}
                setSelectedDocuments={setSelectedDocuments}
                onlyAllowOneDoc={onlyAllowOneDoc}
              />
            )}
            {activeStep === PROCEDURE_STEPS.SEARCH && (
              <StepThreeSearchResults
                searchTermQuery={search.query}
                setPreviewDocument={
                  setPreviewDocument as (
                    doc: Document | BaseReferenceDto | UnkeyedDocument,
                  ) => void
                }
                setActiveStep={setActiveStep}
                onSearchTermSelect={handleSearchTermSelect}
                selectedCountryCode={selectedCountryCode}
                selectedDocuments={selectedDocuments}
                setSelectedDocuments={setSelectedDocuments}
                onlyAllowOneDoc={onlyAllowOneDoc}
              />
            )}
            {activeStep === PROCEDURE_STEPS.PREVIEW && (
              <DocumentPreview
                previewDocument={previewDocument}
                handleGoBack={() => handleBackButtonClick(onClose)}
              />
            )}
          </div>
        </div>
      </PathwayModal.Body>
      {!isPreviewResource && (
        <PathwayModal.Footer>
          <AddProcedureFooter
            plan={plan}
            isInSection={isInSection}
            sectionId={sectionId}
            adminAddingProcedure={adminAddingProcedure}
            onClose={handleOnClose}
            selectedDocuments={selectedDocuments}
            setSelectedDocuments={setSelectedDocuments}
            onCustomAddProcedure={onCustomAddProcedure}
            textVariant={textVariant}
          />
        </PathwayModal.Footer>
      )}
    </PathwayModal>
  );
};

export default AddProcedurePopup;
